import { createFocusTrap } from "focus-trap";
import { add, listen, qs, remove } from "@fluorescent/dom";
import { formatMoney } from "@/neon";
import { disableBodyScroll, enableBodyScroll } from "body-scroll-lock";

import { on } from "@/neon/events";
import { getSizedImageUrl } from "@shopify/theme-images";
import { renderUnitPrice } from "@/lib/unit-pricing";

const classes = {
  visible: "is-visible",
  active: "active",
  fixed: "is-fixed",
};

const selectors = {
  productTitle: "[data-store-availability-product-title]",
  variantTitle: "[data-store-availability-variant-title]",
  productCard: "[data-store-availability-product]",
  storeListcontainer: "[data-store-list-container]",
  wash: "[data-store-availability-modal-wash]",
};

const storeAvailabilityModal = node => {
  var focusTrap = createFocusTrap(node, { allowOutsideClick: true });
  const wash = qs(selectors.wash, node.parentNode);
  const productCard = qs(selectors.productCard, node);
  const storeListContainer = qs(selectors.storeListcontainer, node);

  const events = [
    listen(wash, "click", e => {
      e.preventDefault();
      _close();
    }),
    listen(node, "keydown", ({ keyCode }) => {
      if (keyCode === 27) _close();
    }),
    on("availability:showMore", ({ product, variant, storeList, options }) => {
      productCard.innerHTML = _renderProductCard(product, variant, options);
      _renderAvailabilityList(storeList);
      _open();
    }),
  ];

  const _renderAvailabilityList = storeList => {
    storeListContainer.innerHTML = "";
    storeListContainer.appendChild(storeList);
  };

  const _renderProductCard = (
    { featured_image: image, title },
    {
      title: variant_title,
      featured_image,
      price,
      unit_price,
      unit_price_measurement,
    },
    { hideVariantTitle }
  ) => {
    let productImage = _getVariantImage(image, featured_image);

    return `
      <div class="store-availbility-modal__product-card">
        ${
          productImage
            ? `
            <div class='store-availbility-modal__product-card-image'>
              <img src='${productImage}' alt='${title}'/>
            </div>
          `
            : ""
        }
        <div class='store-availbility-modal__product-card-details'>
          <div>
            <h4>
              <span>${title}</span>
            </h4>
            <div class="store-availbility-modal__product-price-wrapper">
              <span class="store-availbility-modal__product-price">${formatMoney(
                price
              )}</span>
              ${renderUnitPrice(unit_price, unit_price_measurement)}
            </div>
            <div class="store-availbility-modal__product-card-variant${
              hideVariantTitle ? " hidden" : ""
            }">
              ${variant_title}
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const _getVariantImage = (productImage, variantImage) => {
    if (!productImage && !variantImage) return "";

    if (variantImage) {
      return _updateImageSize(variantImage.src);
    }

    return _updateImageSize(productImage);
  };

  const _updateImageSize = imageUrl => {
    return getSizedImageUrl(imageUrl, "200x");
  };

  const _open = () => {
    // Due to this component being shared between templates we have to
    // animate around it being fixed to the window
    add(node, classes.fixed);

    setTimeout(() => {
      add(node, classes.visible);
      add(node, classes.active);
    }, 50);

    node.setAttribute("aria-hidden", "false");
    focusTrap.activate();
    disableBodyScroll(node, {
      allowTouchMove: el => {
        while (el && el !== document.body) {
          if (el.getAttribute("data-scroll-lock-ignore") !== null) {
            return true;
          }
          el = el.parentNode;
        }
      },
      reserveScrollBarGap: true,
    });
  };

  const _close = () => {
    focusTrap.deactivate();
    remove(node, classes.active);
    remove(node, classes.visible);
    node.setAttribute("aria-hidden", "true");
    enableBodyScroll(node);

    setTimeout(() => {
      remove(node, classes.fixed);
    }, 300);
  };

  const unload = () => {
    events.forEach(unsubscribe => unsubscribe());
  };

  return { unload };
};

export default storeAvailabilityModal;
